import {
  FunctionComponent,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { graphql, useQueryLoader } from 'react-relay';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { Combobox } from '@headlessui/react';
import { debounce } from 'lodash-es';

import { EdSkeleton } from 'shared-ui';

import {
  IdentityUserSearchBoxOptions,
  IdentityUserSearchResult,
} from './IdentityUserSearchBoxOptions';

import {
  IdentityUserFilter,
  IdentityUserSearchBoxQuery,
} from './__generated__/IdentityUserSearchBoxQuery.graphql';

export const identityUserSearchBoxQueryDef = graphql`
  query IdentityUserSearchBoxQuery($where: IdentityUserFilter!) {
    list: identityUsers(order: [{ firstName: ASC }], where: $where) {
      __id
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      edges {
        cursor
        node {
          id
          code
          identityUserId
          lastLoggedIn
          updatedAt
          createdAt
          firstName
          lastName
          email
          accountTypes
        }
      }
    }
  }
`;

type Props = {
  onSelected: (result: IdentityUserSearchResult) => void;
  defaultValue?: IdentityUserSearchResult | null;
  showLabel?: boolean;
  placeholder?: string;
};

export const IdentityUserSearchBox: FunctionComponent<Props> = ({
  onSelected,
  defaultValue,
  showLabel,
  placeholder,
}) => {
  const [selected, setSelected] = useState<IdentityUserSearchResult | null>(
    defaultValue ?? null,
  );

  const [searchTerm, setSearchTerm] = useState<string>('');

  const onQueryChangeHandler = useMemo(
    () =>
      debounce(q => {
        setSearchTerm(q);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      onQueryChangeHandler.cancel();
    };
  }, []);

  const onSetSelected = useCallback(
    (r: IdentityUserSearchResult) => {
      setSelected(r);
      onSelected(r);
    },
    [selected],
  );

  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<IdentityUserSearchBoxQuery>(identityUserSearchBoxQueryDef);

  useEffect(() => {
    if (!searchTerm) {
      return;
    }

    const where: IdentityUserFilter = {
      or: [
        { firstName: { startsWith: searchTerm } },
        { lastName: { startsWith: searchTerm } },
        { email: { startsWith: searchTerm } },
      ],
    };
    loadQuery({ where }, { fetchPolicy: 'network-only' });
  }, [searchTerm]);

  return (
    <Combobox as='div' value={selected} onChange={onSetSelected}>
      {showLabel && (
        <Combobox.Label className='block text-sm font-medium text-gray-700'>
          Search Identities
        </Combobox.Label>
      )}
      <div className='relative'>
        <Combobox.Input
          aria-label={placeholder || 'Search Identities'}
          placeholder={placeholder || 'Search Identities'}
          className='focus:border-info-500 focus:ring-info-500 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:outline-none focus:ring-1 sm:text-sm'
          onChange={event => onQueryChangeHandler(event.target.value)}
          displayValue={(result: IdentityUserSearchResult) => {
            if (result) {
              return `${result.firstName} ${result.lastName} (${result.email})`;
            }
            return '';
          }}
          autoComplete='off'
        />
        <Combobox.Button
          aria-label='Identity Search Box'
          className='absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none'
        >
          <ChevronUpDownIcon
            className='h-5 w-5 text-gray-600'
            aria-hidden='true'
          />
        </Combobox.Button>

        {queryReference && (
          <Suspense fallback={<EdSkeleton />}>
            <IdentityUserSearchBoxOptions queryRef={queryReference} />
          </Suspense>
        )}
      </div>
    </Combobox>
  );
};
